import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator, ComparisonOperator, FilterActionType } from '@common/enums';
import { BackupReport, ExportToCSVParams, OdataObject, ReportSendingOptions, UserReport, UserReportsSummaryOData } from '@common/models';
import { ODataPagedResult, ODataService } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { ReportsUserTag, ReportsUserTagsMap } from '@common/services/smart-search';
import { FilterOptions } from '@common/types';
import { filterByWords } from '@common/utils';
import { containsWrapper, getFilterByContains, getFilterByDate } from '@common/utils/functions/search';
import cloneDeep from 'lodash/cloneDeep';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ReportsService extends SharedOdataService<any> {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly http = inject(HttpClient);

  readonly #license = ReportsUserTagsMap.get(ReportsUserTag.License);
  readonly #userName = ReportsUserTagsMap.get(ReportsUserTag.UserName);
  readonly #userState = ReportsUserTagsMap.get(ReportsUserTag.UserState);

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;
  protected readonly isSingleUser = this.ability.isSingleUser;

  constructor() {
    super('Reports');
  }

  backupReports(): Observable<BackupReport> {
    return this.isAdmin() ? this.domainBackupReport(this.authDomainId()) : this.userBackupReport(this.authUserId());
  }

  private domainBackupReport(id: string): Observable<BackupReport> {
    const odataService = this.odataFactory.CreateService<BackupReport>(`/Domains(${id})/BackupReport`);

    return this.http.get<BackupReport>(odataService.Query().GetUrl());
  }

  private userBackupReport(id: string): Observable<BackupReport> {
    const odataService = this.odataFactory.CreateService<BackupReport>(`/SingleUsers(${id})/BackupReport`);

    return this.http.get<BackupReport>(odataService.Query().GetUrl());
  }

  userReports(): Observable<ODataPagedResult<UserReport>> {
    const request$ = this.isAdmin() ? this.domainUserReport(this.authDomainId()) : this.singleUserReport(this.authUserId());

    return request$.pipe(
      switchMap((oDataUserReports: ODataPagedResult<UserReport>) => {
        const payload = {
          UserIds: oDataUserReports.data.map((user) => user.UserId)
        };

        return forkJoin([this.userReportLicences(payload), of(oDataUserReports)]);
      }),
      map(([licenses, oDataUserReports]) => {
        const clone = cloneDeep(oDataUserReports);

        clone.data = clone.data.map((item) => {
          const license = licenses.find((l) => l.UserId === item.UserId);

          return { ...item, ...license };
        });

        return clone;
      })
    );
  }

  private domainUserReport(id: string): Observable<ODataPagedResult<UserReport>> {
    const odataService = this.odataFactory.CreateService<UserReport>(`/Domains(${id})/UserReports`);

    return this.fetchData<UserReport>(odataService);
  }

  private singleUserReport(id: string): Observable<ODataPagedResult<UserReport>> {
    const odataService = this.odataFactory.CreateService<UserReport>(`/SingleUsers(${id})/UserReports`);

    return this.fetchData<UserReport>(odataService);
  }

  getReportSendingOptions(): Observable<ReportSendingOptions> {
    return this.reportSendingOptions(this.isAdmin() ? this.authDomainId() : this.authUserId());
  }

  setReportSendingOptions(payload: ReportSendingOptions): Observable<ReportSendingOptions> {
    return this.reportSendingOptions(this.isAdmin() ? this.authDomainId() : this.authUserId(), payload);
  }

  private reportSendingOptions(id: string, payload?: ReportSendingOptions): Observable<ReportSendingOptions> {
    const odataService = this.odataFactory.CreateService<ReportSendingOptions>(`/ReportSendingOptions(${id})`);

    if (payload) {
      payload.Id = id;

      return this.http.put<ReportSendingOptions>(odataService.Query().GetUrl(), payload);
    } else {
      return this.http.get<ReportSendingOptions>(odataService.Query().GetUrl());
    }
  }

  getUserReportsSummary(): Observable<UserReportsSummaryOData> {
    return this.isAdmin() ? this.domainSummary(this.authDomainId()) : this.singleUserSummary(this.authUserId());
  }

  private domainSummary(id: string): Observable<UserReportsSummaryOData> {
    const odataService = this.odataFactory.CreateService<UserReportsSummaryOData>(`/Domains(${id})/UserReportsSummary`);

    return this.http.get<UserReportsSummaryOData>(odataService.Query().GetUrl());
  }

  private singleUserSummary(id: string): Observable<UserReportsSummaryOData> {
    const odataService = this.odataFactory.CreateService<UserReportsSummaryOData>(`/SingleUsers(${id})/UserReportsSummary`);

    return this.http.get<UserReportsSummaryOData>(odataService.Query().GetUrl());
  }

  exportToCSV(report: 'backup' | 'user', params: ExportToCSVParams): Observable<Blob> {
    const defaultOptions = { report, params };

    return this.isAdmin()
      ? this.domainExportToCSV(Object.assign(defaultOptions, { id: this.authDomainId() }))
      : this.singleUserExportToCSV(Object.assign(defaultOptions, { id: this.authUserId() }));
  }

  private domainExportToCSV(options: { id: string; report: 'backup' | 'user'; params: ExportToCSVParams }): Observable<Blob> {
    const { id, report, params } = options;
    const paramsString = Object.entries(params)
      .map((key) => key.join('='))
      .join('&');
    let odataService: ODataService<Blob>;

    if (report === 'backup') {
      odataService = this.odataFactory.CreateService<Blob>(`/Domains(${id})/DownloadBackupReport?${paramsString}`);
    }

    if (report === 'user') {
      odataService = this.odataFactory.CreateService<Blob>(`/Domains(${id})/DownloadUserReports?${paramsString}`);
    }

    return this.http.get(odataService.Query().GetUrl(), { responseType: 'blob' });
  }

  private singleUserExportToCSV(options: { id: string; report: 'backup' | 'user'; params: ExportToCSVParams }): Observable<Blob> {
    const { id, report, params } = options;
    const paramsString = Object.entries(params)
      .map((key) => key.join('='))
      .join('&');
    let odataService: ODataService<Blob>;

    if (report === 'backup') {
      odataService = this.odataFactory.CreateService(`/SingleUsers(${id})/DownloadBackupReport?${paramsString}`);
    }

    if (report === 'user') {
      odataService = this.odataFactory.CreateService(`/SingleUsers(${id})/DownloadUserReports?${paramsString}`);
    }

    return this.http.get(odataService.Query().GetUrl(), { responseType: 'blob' });
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];
    const isResetButtonClick = Object.keys(searchObj).length === 0;

    if (searchObj[this.#userName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#userName.tag] as SmartSearchModelField[],
        prop: this.#userName.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#license.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#license.tag] as SmartSearchModelField[],
        prop: this.#license.prop
      };

      filter.push(getFilterByDate(options));
    }

    if (searchObj[this.#userState.tag]) {
      const userState = searchObj[this.#userState.tag][0];

      switch (userState.value) {
        case FilterActionType.ContainsIssue:
          filter.push(`${this.#userState.prop} ${ComparisonOperator.Eq} true`);
          filter.push(`ContainsIssue ${ComparisonOperator.Eq} true`);
          break;
        case FilterActionType.EnabledUsers:
          filter.push(`${this.#userState.prop} ${ComparisonOperator.Eq} true`);
          break;
        case FilterActionType.DisabledUsers:
          filter.push(`${this.#userState.prop} ${ComparisonOperator.Eq} false`);
          break;
        default:
          console.error('Caught error manually: Unknown sorting type');
      }
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');

      filter.push(containsWrapper(this.#userName.prop, term));
    }

    if (isResetButtonClick) {
      filter.push(`${this.#userState.prop} ${ComparisonOperator.Eq} true`);
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }

  userReportLicences(payload: { UserIds: string[] }): Observable<UserReportLicence[]> {
    return this.isSingleUser()
      ? this.singleUserReportLicences(this.authUserId(), payload)
      : this.domainReportLicences(this.authDomainId(), payload);
  }

  private domainReportLicences(domainId: string, payload: { UserIds: string[] }): Observable<UserReportLicence[]> {
    const odataService = this.odataFactory.CreateService(`/Domains(${domainId})/UserLicenses`);

    return this.http.post<OdataObject<UserReportLicence[]>>(odataService.Query().GetUrl(), payload).pipe(map((res) => res.value));
  }

  private singleUserReportLicences(userId: string, payload: { UserIds: string[] }): Observable<UserReportLicence[]> {
    const odataService = this.odataFactory.CreateService(`/SingleUsers(${userId})/UserLicenses`);

    return this.http.post<OdataObject<UserReportLicence[]>>(odataService.Query().GetUrl(), payload).pipe(map((res) => res.value));
  }
}

interface UserReportLicence {
  ExpireDate: string;
  IsLicense: boolean;
  IsTrial: boolean;
  UserId: string;
}
